import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../Context/auth';
import MenuLateral from "../Components/MenuPrincipal/MenuLateral";
import ProducaoForm from '../Components/ProducaoForm/ProducaoForm';
import ModalConcluirProducao from '../Components/ProducaoForm/ModalConcluirProducao';
import ModalEstornarConclusao from '../Components/ProducaoForm/ModalEstornarConclusao';
import ModalBaixarInsumo from '../Components/ProducaoForm/ModalBaixarInsumo';
import ModalEstornarInsumo from '../Components/ProducaoForm/ModalEstornarInsumo';
import './Producao.css'; // Updated import

function EditarProducao() {
    const { currentUser } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();

    const [loadingData, setLoadingData] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [isConcluirModalVisible, setIsConcluirModalVisible] = useState(false);
    const [isEstornarModalVisible, setIsEstornarModalVisible] = useState(false);
    const [isBaixarInsumoModalVisible, setIsBaixarInsumoModalVisible] = useState(false);
    const [isEstornarInsumoModalVisible, setIsEstornarInsumoModalVisible] = useState(false);
    const [selectedInsumo, setSelectedInsumo] = useState(null);
    const [activeTab, setActiveTab] = useState('producao');

    const apiCall = useCallback(async (url, method = 'GET', data = null) => {
        if (!currentUser) throw new Error("Usuário não autenticado.");
        const token = await currentUser.getIdToken();
        const API_URL = process.env.REACT_APP_API_URL;
        const config = { method, headers: { 'Authorization': `Bearer ${token}` } };
        if (data && method !== 'GET' && method !== 'HEAD') {
            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify(data);
        }
        const response = await fetch(`${API_URL}${url}`, config);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Falha na requisição para ${url} (${response.status})`);
        }
        if (response.status === 204) return null;
        return response.json();
    }, [currentUser]);

    const fetchProducaoData = useCallback(async () => {
        if (!id || !currentUser) {
            setLoadingData(false);
            return;
        }
        setLoadingData(true);
        setInitialData(null);

        try {
            const data = await apiCall(`/api/producao/${id}`);
            if (data && data.id) {
                setInitialData(data);
            } else {

            }
        } catch (error) {

            Swal.fire('Erro', error.message, 'error');
        } finally {
            setLoadingData(false);
        }
    }, [id, currentUser, apiCall]);

    useEffect(() => {
        fetchProducaoData();
    }, [fetchProducaoData]);

    const handleUpdate = async (formData) => {

        setLoadingSubmit(true);
        try {
            const dataToSend = { ...formData, status: initialData?.status === 'Cancelada' ? 'Cancelada' : formData.status };
            await apiCall(`/api/producao/${id}`, 'PUT', dataToSend);
            Swal.fire({
                icon: 'success',
                title: 'Sucesso!',
                text: 'Ordem de Produção atualizada!',
                confirmButtonColor: '#0d6efd'
            });
            navigate('/app/producao');
        } catch (error) {

            Swal.fire('Erro', error.message, 'error');
        } finally {
            setLoadingSubmit(false);
        }
    };

    // Actions Handlers
    const handleConcluirClick = () => {
        if (initialData?.status === 'Concluída' || initialData?.status === 'Cancelada') return;
        setIsConcluirModalVisible(true);
    };

    const handleEstornarClick = () => {
        if (initialData?.status === 'Planejada' || initialData?.status === 'Cancelada') return;
        setIsEstornarModalVisible(true);
    };

    const handleCancelarProducao = async () => {
        if (initialData?.status === 'Concluída' || initialData?.status === 'Cancelada') return;

        Swal.fire({
            title: "Cancelar Produção?",
            text: "Esta ação definirá o status como 'Cancelada'.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Sim, cancelar!",
            cancelButtonText: "Não"
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoadingSubmit(true);
                try {
                    const formData = {
                        data_producao: initialData.data_producao,
                        descricao: initialData.descricao,
                        observacoes: initialData.observacoes,
                        status: 'Cancelada',
                        itens: initialData.itens_produzir || []
                    };
                    await apiCall(`/api/producao/${id}`, 'PUT', formData);
                    Swal.fire("Cancelada!", "A Ordem de Produção foi cancelada.", "success");
                    setInitialData(prev => prev ? { ...prev, status: 'Cancelada' } : null);
                } catch (error) {
                    Swal.fire('Erro', error.message, 'error');
                } finally {
                    setLoadingSubmit(false);
                }
            }
        });
    };

    // Insumo Handlers
    const handleOpenBaixarModal = (insumo) => { setSelectedInsumo(insumo); setIsBaixarInsumoModalVisible(true); };
    const handleOpenEstornarModal = (insumo) => { setSelectedInsumo(insumo); setIsEstornarInsumoModalVisible(true); };
    const handleDeleteInsumo = async (insumo) => {
        if (insumo.status !== 'Em aberto' || initialData?.status !== 'Planejada') {
            Swal.fire("Atenção", "Só é possível excluir insumos 'Em aberto' de OPs 'Planejadas'.", "warning");
            return;
        }
        Swal.fire({
            title: "Excluir Insumo?",
            text: `Confirmar exclusão de "${insumo.insumo_descricao}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Sim, excluir"
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoadingSubmit(true);
                try {
                    await apiCall(`/api/producao/insumo/${insumo.id}`, 'DELETE');
                    Swal.fire("Excluído!", "Insumo removido.", "success");
                    fetchProducaoData();
                } catch (error) {
                    Swal.fire("Erro!", error.message, "error");
                } finally {
                    setLoadingSubmit(false);
                }
            }
        });
    };

    const onInsumoActionSuccess = () => {
        setIsBaixarInsumoModalVisible(false);
        setIsEstornarInsumoModalVisible(false);
        setSelectedInsumo(null);
        fetchProducaoData();
    };

    // Refresh Insumos
    const podeAtualizarInsumos = useMemo(() => {
        if (!initialData || initialData.status !== 'Planejada') return false;
        return !initialData.insumos_necessarios?.some(ins => parseFloat(ins.quantidade_baixada) > 0);
    }, [initialData]);

    const handleRefreshInsumos = () => {
        if (!podeAtualizarInsumos) return;
        Swal.fire({
            title: "Atualizar Insumos?",
            text: "Os insumos serão recalculados com base na ficha técnica atual.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sim, atualizar"
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoadingSubmit(true);
                try {
                    await apiCall(`/api/producao/${id}/refresh-insumos`, 'POST');
                    Swal.fire("Sucesso!", "Insumos atualizados.", "success");
                    fetchProducaoData();
                } catch (error) {
                    Swal.fire('Erro', error.message, 'error');
                } finally {
                    setLoadingSubmit(false);
                }
            }
        });
    };

    // Print Handlers
    const handlePrint = async (endpoint, successMsg) => {
        setLoadingSubmit(true);

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/producao/${id}/${endpoint}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Falha ao gerar documento.");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url);

        } catch (error) {
            Swal.fire('Erro', error.message, 'error');

        } finally {
            setLoadingSubmit(false);
        }
    };

    const podeConcluir = initialData?.status && !['Concluída', 'Cancelada'].includes(initialData.status);
    const podeEstornar = initialData?.status && !['Planejada', 'Cancelada'].includes(initialData.status);
    const podeCancelar = initialData?.status && !['Concluída', 'Cancelada'].includes(initialData.status);
    // Removed duplicate declaration

    // Missing declarations from previous accidental removal (restoring them correctly this time)
    const [showImportarModal, setShowImportarModal] = useState(false);

    return (
        <div className="form-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">
                <div className="page-header mb-2 d-flex justify-content-between align-items-center px-4">
                    <div className="d-flex align-items-center gap-3">
                        <button className="btn btn-link text-secondary p-0" onClick={() => navigate('/app/producao')} title="Voltar">
                            <i className="fas fa-arrow-left fa-lg"></i>
                        </button>
                        <h1 className="page-title mb-0">Editar <span className="highlight">Produção {initialData?.numero}</span></h1>
                    </div>

                    {!loadingData && initialData && (
                        <div className="d-flex gap-2">
                            {/* Botão Importar Pedidos */}
                            {initialData.status === 'Planejada' && (
                                <button
                                    className="btn btn-primary-custom"
                                    onClick={() => setShowImportarModal(true)}
                                    title="Importar itens de pedidos"
                                >
                                    <i className="fas fa-file-import me-2"></i>Importar de Pedidos
                                </button>
                            )}

                            {/* Dropdown Ações */}
                            {(podeConcluir || podeEstornar || podeAtualizarInsumos || podeCancelar) && (
                                <div className="btn-group">
                                    <button className="btn-primary-custom dropdown-toggle" data-bs-toggle="dropdown" disabled={loadingSubmit}>
                                        <i className="fas fa-ellipsis-v me-2"></i> Ações
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-end shadow dropdown-menu-custom">
                                        {podeConcluir && (
                                            <li>
                                                <button className="dropdown-item action-item action-concluir" onClick={handleConcluirClick}>
                                                    <i className="fas fa-check-circle me-2"></i> Concluir Item
                                                </button>
                                            </li>
                                        )}
                                        {podeEstornar && (
                                            <li>
                                                <button className="dropdown-item action-item action-estornar" onClick={handleEstornarClick}>
                                                    <i className="fas fa-undo-alt me-2"></i> Estornar Conclusão
                                                </button>
                                            </li>
                                        )}
                                        {podeAtualizarInsumos && (
                                            <li>
                                                <button className="dropdown-item action-item action-atualizar" onClick={handleRefreshInsumos}>
                                                    <i className="fas fa-sync-alt me-2"></i> Atualizar Insumos
                                                </button>
                                            </li>
                                        )}
                                        {(podeConcluir || podeEstornar || podeAtualizarInsumos) && podeCancelar && (
                                            <li><hr className="dropdown-divider" /></li>
                                        )}
                                        {podeCancelar && (
                                            <li>
                                                <button className="dropdown-item action-item action-cancelar" onClick={handleCancelarProducao}>
                                                    <i className="fas fa-ban me-2"></i> Cancelar Produção
                                                </button>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            )}

                            {/* Dropdown Imprimir */}
                            <div className="btn-group">
                                <button className="btn-primary-custom dropdown-toggle" data-bs-toggle="dropdown" disabled={loadingSubmit}>
                                    <i className="fas fa-print me-2"></i> Imprimir
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end shadow">
                                    <li><button className="dropdown-item" onClick={() => handlePrint('imprimir', 'Resumo')}>Resumo de Produção</button></li>
                                    <li><button className="dropdown-item" onClick={() => handlePrint('imprimir-insumos', 'Insumos')}>Relatório de Insumos</button></li>
                                    <li><button className="dropdown-item" onClick={() => handlePrint('imprimir-fichas', 'Fichas')}>Fichas de Produção</button></li>
                                    <li><hr className="dropdown-divider" /></li>
                                    <li><button className="dropdown-item" onClick={() => handlePrint('etiquetas-individuais', 'Etiquetas')}>Etiqueta Individual</button></li>
                                    <li><button className="dropdown-item" onClick={() => handlePrint('etiquetas-coletivas', 'Etiquetas')}>Etiqueta Coletiva</button></li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>

                <div className="producao-content px-4">
                    <div className="form-card-container">
                        {!loadingData && initialData && (
                            <>
                                <ul className="nav nav-tabs mb-2">
                                    {[
                                        { key: 'producao', label: 'Produção' },
                                        { key: 'insumos', label: 'Insumos' },
                                        { key: 'conclusoes', label: 'Conclusões' },
                                        { key: 'pedidos', label: 'Pedidos' }
                                    ].map(tab => (
                                        <li className="nav-item" key={tab.key}>
                                            <button
                                                className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                                                onClick={() => setActiveTab(tab.key)}
                                            >
                                                {tab.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>

                                <ProducaoForm
                                    onSubmit={handleUpdate}
                                    initialData={initialData}
                                    isEditing={true}
                                    loadingSubmit={loadingSubmit}
                                    activeTab={activeTab}
                                    onBaixarInsumo={handleOpenBaixarModal}
                                    onEstornarInsumo={handleOpenEstornarModal}
                                    onDeleteInsumo={handleDeleteInsumo}
                                    onDataRefreshNeeded={fetchProducaoData}
                                    onConcluirSucesso={() => { setIsConcluirModalVisible(false); fetchProducaoData(); }}
                                    externalIsModalImportarVisible={showImportarModal}
                                    externalSetIsModalImportarVisible={setShowImportarModal}
                                    isConcluirModalVisible={isConcluirModalVisible}
                                    setIsConcluirModalVisible={setIsConcluirModalVisible}
                                />
                            </>
                        )}
                        {loadingData && (
                            <div className="loading-container">
                                <div className="spinner-border text-primary"></div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            {isConcluirModalVisible && initialData && (
                <ModalConcluirProducao
                    isVisible={isConcluirModalVisible}
                    onClose={() => setIsConcluirModalVisible(false)}
                    producaoId={initialData.id}
                    numeroOp={initialData.numero}
                    itensProduzir={initialData.itens_produzir || []}
                    onConclusaoSucesso={() => { setIsConcluirModalVisible(false); fetchProducaoData(); }}
                />
            )}

            {isEstornarModalVisible && initialData && (
                <ModalEstornarConclusao
                    isVisible={isEstornarModalVisible}
                    onClose={() => setIsEstornarModalVisible(false)}
                    producaoId={initialData.id}
                    numeroOp={initialData.numero}
                    onEstornoSucesso={() => { setIsEstornarModalVisible(false); fetchProducaoData(); }}
                />
            )}

            {isBaixarInsumoModalVisible && selectedInsumo && (
                <ModalBaixarInsumo
                    isVisible={isBaixarInsumoModalVisible}
                    onClose={() => { setIsBaixarInsumoModalVisible(false); setSelectedInsumo(null); }}
                    insumo={selectedInsumo}
                    onBaixaSucesso={onInsumoActionSuccess}
                />
            )}

            {isEstornarInsumoModalVisible && selectedInsumo && (
                <ModalEstornarInsumo
                    isVisible={isEstornarInsumoModalVisible}
                    onClose={() => { setIsEstornarInsumoModalVisible(false); setSelectedInsumo(null); }}
                    insumo={selectedInsumo}
                    onEstornoSucesso={onInsumoActionSuccess}
                />
            )}

        </div>
    );
}

export default EditarProducao;